import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, auth, authSessionApi, type ActiveRoleRequest } from '../api/client';
import { getDefaultRoute } from '../config/defaultRoute';
import { takeEarlySession } from '../api/earlySession';
import { CONSENT_ROUTE } from '../config/consentGate';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  RequiredConsentDto,
  UserResponse,
} from '../api/generated';

// The generated AuthResponse type predates the backend's `mustChangePassword` field
// (forced password rotation for bootstrap/admin-created accounts). Augment it locally rather
// than hand-editing the generated OpenAPI client.
type LoginResponse = AuthResponse & {
  mustChangePassword?: boolean;
  requiresRoleSelection?: boolean;
  availableRoles?: unknown;
  activeRole?: string;
  roleSelectionToken?: string;
  pendingConsents?: RequiredConsentDto[];
};

interface AuthContextType {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  assignedRoles: string[];
  availableRoles: string[];
  activeRole: string | null;
  requiresRoleSelection: boolean;
  hasActiveRole: boolean;
  /** Required consents (Terms, Privacy Policy) still to accept at the version in force. */
  pendingConsents: RequiredConsentDto[];
  /** True while the user must go through the consent gate before any protected page. */
  requiresConsent: boolean;
  /** Accepts the current version of every pending required consent; resolves to what is still pending. */
  acceptRequiredConsents: () => Promise<RequiredConsentDto[]>;
  /** Participant refuses the pending required consents; the team is notified and access stays blocked. */
  declineRequiredConsents: () => Promise<RequiredConsentDto[]>;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  selectRole: (role: string) => Promise<void>;
  switchRole: (role: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_KEY = 'peoplerock_user';
const ROLE_SELECTION_TOKEN_KEY = 'peoplerock_role_selection_token';
const ACTIVE_ROLE_KEY = 'peoplerock_active_role';
const LAST_ROLE_KEY_PREFIX = 'peoplerock_last_role:';

const normalizeRoleName = (role: unknown): string => {
  if (typeof role !== 'string') return '';
  return role.trim().toUpperCase().replace(/^ROLE_/, '');
};

const normalizeRoleNames = (roles: unknown): string[] => {
  if (!roles) return [];

  let roleArray: unknown[] = [];

  if (Array.isArray(roles)) {
    roleArray = roles;
  } else if (roles instanceof Set) {
    roleArray = Array.from(roles);
  } else if (typeof roles === 'string') {
    roleArray = [roles];
  } else if (typeof roles === 'object') {
    const maybeIterable = roles as unknown as { [Symbol.iterator]?: () => Iterator<unknown> };

    if (typeof maybeIterable[Symbol.iterator] === 'function') {
      roleArray = Array.from(roles as Iterable<unknown>);
    } else {
      const roleRecord = roles as Record<string, unknown>;
      const roleKeys = Object.keys(roleRecord).filter((key) => key.startsWith('ROLE_'));
      const roleValues = Object.values(roleRecord).filter((value): value is string => typeof value === 'string');
      roleArray = [...roleKeys, ...roleValues];
    }
  }

  return Array.from(new Set(
    roleArray
    .map((role) => {
      if (typeof role === 'string') return role;
      if (role && typeof role === 'object' && 'name' in role) {
        const roleName = (role as { name?: unknown }).name;
        return typeof roleName === 'string' ? roleName : '';
      }
      return '';
    })
    .filter((role): role is string => !!role)
  )).map(normalizeRoleName).filter(Boolean);
};

const roleMatches = (activeRole: string | null, requiredRole: string): boolean =>
  !!activeRole && normalizeRoleName(activeRole) === normalizeRoleName(requiredRole);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [requiresRoleSelection, setRequiresRoleSelection] = useState(false);
  const [pendingConsents, setPendingConsents] = useState<RequiredConsentDto[]>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const syncAuthState = useCallback((authData: LoginResponse) => {
    const nextUser = authData.user;
    const nextAvailableRoles = normalizeRoleNames(authData.availableRoles ?? nextUser?.roles);
    const responseActiveRole = normalizeRoleName(authData.activeRole);
    const nextActiveRole = responseActiveRole
      || (authData.accessToken && nextAvailableRoles.length === 1 ? nextAvailableRoles[0] : null);

    if (nextUser) {
      setUser(nextUser);
      localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    }

    setAvailableRoles(nextAvailableRoles);
    setActiveRole(nextActiveRole || null);
    setPendingConsents(authData.pendingConsents ?? []);
    setRequiresRoleSelection(Boolean(authData.requiresRoleSelection) || (
      !authData.accessToken && nextAvailableRoles.length !== 1
    ));

    if (authData.roleSelectionToken) {
      sessionStorage.setItem(ROLE_SELECTION_TOKEN_KEY, authData.roleSelectionToken);
    } else if (authData.accessToken) {
      sessionStorage.removeItem(ROLE_SELECTION_TOKEN_KEY);
    }

    if (nextActiveRole) {
      sessionStorage.setItem(ACTIVE_ROLE_KEY, nextActiveRole);
    } else {
      sessionStorage.removeItem(ACTIVE_ROLE_KEY);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      const token = auth.getToken();
      const storedUser = localStorage.getItem(USER_KEY);
      const selectionToken = sessionStorage.getItem(ROLE_SELECTION_TOKEN_KEY);

      if (token) {
        // The session check may already be in flight: src/earlySession.ts starts it while the app
        // bundle is still downloading.
        const earlySession = takeEarlySession();
        try {
          const response = await (earlySession ?? authSessionApi.getSession());
          if (!mounted) return;
          // Copy a legacy localStorage token into this tab once its session is valid.
          auth.setToken(token);
          syncAuthState(response.data as LoginResponse);
        } catch (error) {
          const status = (error as { response?: { status?: number; data?: { error?: string } } })?.response?.status;
          const errorCode = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
          if (earlySession && status === 403 && errorCode === 'PASSWORD_CHANGE_REQUIRED' && window.location.pathname !== '/change-password') {
            // The early request bypassed the axios interceptor that sends such accounts to the password change page.
            window.location.assign('/change-password');
          }
          if (mounted && status === 403 && errorCode === 'PASSWORD_CHANGE_REQUIRED' && storedUser) {
            const currentUser = JSON.parse(storedUser) as UserResponse;
            setUser(currentUser);
            setAvailableRoles(normalizeRoleNames(currentUser.roles));
            setActiveRole(normalizeRoleName(sessionStorage.getItem(ACTIVE_ROLE_KEY)) || null);
            setRequiresRoleSelection(false);
          } else if (mounted) {
            console.error('Token validation failed:', error);
            auth.clearToken();
            localStorage.removeItem(USER_KEY);
            sessionStorage.removeItem(ROLE_SELECTION_TOKEN_KEY);
            sessionStorage.removeItem(ACTIVE_ROLE_KEY);
            setUser(null);
            setAvailableRoles([]);
            setActiveRole(null);
            setRequiresRoleSelection(false);
          }
        }
      } else if (mounted && selectionToken && storedUser) {
        const currentUser = JSON.parse(storedUser) as UserResponse;
        setUser(currentUser);
        setAvailableRoles(normalizeRoleNames(currentUser.roles));
        setActiveRole(null);
        setRequiresRoleSelection(true);
      }

      if (mounted) setIsLoading(false);
    };

    void initializeAuth();
    return () => {
      mounted = false;
    };
  }, [syncAuthState]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      auth.clearToken();
      sessionStorage.removeItem(ROLE_SELECTION_TOKEN_KEY);
      sessionStorage.removeItem(ACTIVE_ROLE_KEY);
      const loginData: LoginRequest = { identifier: email, password };
      const response = await apiClient.auth.login({ loginRequest: loginData });
      const authData: LoginResponse = response.data;

      if (authData.accessToken) {
        auth.setToken(authData.accessToken);
      }
      syncAuthState(authData);

      queryClient.removeQueries({ queryKey: ['notifications'] });

      const roles = normalizeRoleNames(authData.availableRoles ?? authData.user?.roles);
      if (roles.length > 1 || authData.requiresRoleSelection || !authData.accessToken) {
        navigate('/select-role');
        return;
      }

      if (authData.requiresRoleSelection || !authData.accessToken) {
        navigate('/select-role');
        return;
      }

      if (authData.mustChangePassword) {
        navigate('/change-password');
        return;
      }

      if (authData.pendingConsents?.length) {
        navigate(CONSENT_ROUTE);
        return;
      }

      const defaultRoute = getDefaultRoute(authData.activeRole || roles[0]);
      const redirectTo = sessionStorage.getItem('redirectAfterLogin') || defaultRoute;
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectTo);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [navigate, queryClient, syncAuthState]);

  const register = useCallback(async (userData: RegisterRequest) => {
    try {
      const response = await apiClient.auth.register({ registerRequest: userData });
      const authData: LoginResponse = response.data;
      
      if (authData.accessToken) {
        auth.setToken(authData.accessToken);
      }
      syncAuthState(authData);

      queryClient.removeQueries({ queryKey: ['notifications'] });

      const roles = normalizeRoleNames(authData.availableRoles ?? authData.user?.roles);
      if (roles.length > 1 || authData.requiresRoleSelection || !authData.accessToken) {
        navigate('/select-role');
        return;
      }

      if (authData.pendingConsents?.length) {
        navigate(CONSENT_ROUTE);
        return;
      }

      const defaultRoute = getDefaultRoute(authData.activeRole || roles[0]);
      navigate(defaultRoute);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }, [navigate, queryClient, syncAuthState]);

  const selectRole = useCallback(async (role: string) => {
    const request: ActiveRoleRequest = { role };
    const selectionToken = sessionStorage.getItem(ROLE_SELECTION_TOKEN_KEY);
    if (selectionToken) {
      request.roleSelectionToken = selectionToken;
    }

    const response = await authSessionApi.selectActiveRole(request);
    const authData = response.data as LoginResponse;
    if (!authData.accessToken) {
      throw new Error('The server did not return an access token for the selected role');
    }

    auth.setToken(authData.accessToken);
    syncAuthState(authData);

    const chosenRole = normalizeRoleName(authData.activeRole || role);
    const userId = authData.user?.id ?? user?.id;
    if (userId && chosenRole) {
      localStorage.setItem(`${LAST_ROLE_KEY_PREFIX}${userId}`, chosenRole);
    }

    queryClient.clear();

    if (authData.mustChangePassword) {
      navigate('/change-password');
      return;
    }

    if (authData.pendingConsents?.length) {
      navigate(CONSENT_ROUTE);
      return;
    }

    const defaultRoute = getDefaultRoute(chosenRole);
    const redirectTo = sessionStorage.getItem('redirectAfterLogin') || defaultRoute;
    sessionStorage.removeItem('redirectAfterLogin');
    navigate(redirectTo);
  }, [navigate, queryClient, syncAuthState, user?.id]);

  const switchRole = selectRole;

  const logout = useCallback(() => {
    auth.clearToken();
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(ROLE_SELECTION_TOKEN_KEY);
    sessionStorage.removeItem(ACTIVE_ROLE_KEY);
    setUser(null);
    setAvailableRoles([]);
    setActiveRole(null);
    setRequiresRoleSelection(false);
    setPendingConsents([]);
    queryClient.clear();
    navigate('/');
  }, [navigate, queryClient]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authSessionApi.getSession();
      syncAuthState(response.data as LoginResponse);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  }, [logout, syncAuthState]);

  const acceptRequiredConsents = useCallback(async () => {
    const response = await apiClient.gdpr.acceptRequiredConsents();
    const remaining = response.data ?? [];
    setPendingConsents(remaining);
    return remaining;
  }, []);

  const declineRequiredConsents = useCallback(async () => {
    const response = await apiClient.gdpr.declineRequiredConsents();
    return response.data ?? [];
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    assignedRoles: normalizeRoleNames(user?.roles),
    availableRoles,
    activeRole,
    requiresRoleSelection,
    hasActiveRole: !!activeRole,
    pendingConsents,
    requiresConsent: pendingConsents.length > 0,
    acceptRequiredConsents,
    declineRequiredConsents,
    login,
    register,
    selectRole,
    switchRole,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Same as {@link useAuth}, but returns null when rendered outside an AuthProvider instead
 * of throwing. For optional, nice-to-have reads of the session (e.g. prefilling a form
 * field with the user's own name) that must never break the surrounding feature.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalAuth = (): AuthContextType | null => useContext(AuthContext) ?? null;

/**
 * Hook to check permissions against the one active role in this tab.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useRole = () => {
  const { activeRole } = useAuth();
  
  /**
  * Check if the active session role matches a required role.
   */
  const hasRole = useCallback((roleName: string): boolean => {
    return roleMatches(activeRole, roleName);
  }, [activeRole]);
  
  /**
  * Check if the active session role matches any required role.
   */
  const hasAnyRole = useCallback((roleNames: string[]): boolean => {
    return roleNames.some((roleName) => roleMatches(activeRole, roleName));
  }, [activeRole]);
  
  /**
  * Check if the active session role matches every required role.
   */
  const hasAllRoles = useCallback((roleNames: string[]): boolean => {
    return roleNames.every((roleName) => roleMatches(activeRole, roleName));
  }, [activeRole]);
  
  /**
  * Return the one role effective in this session.
   */
  const getEffectiveUserRoles = useCallback((): string[] => {
    return activeRole ? [activeRole] : [];
  }, [activeRole]);
  
  // Convenience shortcuts for common roles
  const isAdmin = hasRole('ROLE_ADMIN');
  const isTrainer = hasRole('ROLE_TRAINER');
  const isParticipant = hasRole('ROLE_PARTICIPANT');
  
  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getEffectiveUserRoles,
    isAdmin,
    isTrainer,
    isParticipant,
  };
};

// Utility function to get token
// eslint-disable-next-line react-refresh/only-export-components
export const getToken = (): string | null => {
  return auth.getToken();
};

// Utility function to check if authenticated
// eslint-disable-next-line react-refresh/only-export-components
export const isAuthenticated = (): boolean => {
  return auth.isAuthenticated();
};
